import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import Fuse from 'fuse.js'
import { setTag } from '@sentry/browser'
import styled from 'styled-components'
import PropTypes from 'prop-types'

import { Avatar } from './Avatar'
import { cssVariables } from '../../styles'
import { useMerchantSearch } from '../../hooks/useMerchantSearch'
import { getCurrentMerchantID } from '../../features/data'
import { setMerchantIdAndCountryCodeCookies } from '../../utils'
import { useCurrentMerchantCountryCode, usePermissions } from '../../hooks'
import { getMerchantSearchCountry } from '../../hooks/permissionsV2'
import { ErrorMessage } from '../shared'

const AnimatedItem = styled.div`
	width: 100%;
	animation: merchant-picker-item-in 140ms ease-out;

	@keyframes merchant-picker-item-in {
		from {
			opacity: 0;
			transform: scale(0.98);
		}
		to {
			opacity: 1;
			transform: scale(1);
		}
	}
`

const MerchantInput = styled.input`
	width: 100%;
	box-sizing: border-box;
	min-height: 48px;
	padding: 14px 16px;
	border: 1px solid ${cssVariables.colors.borders.divider};
	border-radius: 8px;
	background: ${cssVariables.colors.background.secondary};
	color: ${cssVariables.colors.text.primary};
	font: inherit;
	transition: border-color 120ms ease;

	&::placeholder {
		color: ${cssVariables.colors.text.secondary};
	}

	&:hover {
		border-color: ${cssVariables.colors.borders.default};
	}

	&:focus-visible {
		border-color: ${cssVariables.colors.brand.secondary};
		outline: 2px solid ${cssVariables.colors.brand.secondary};
		outline-offset: 2px;
	}
`

const SearchModeLabel = styled.label`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	min-height: 44px;
	margin-bottom: 8px;
	color: ${cssVariables.colors.text.primary};
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
`

const SearchModeSwitch = styled.input`
	appearance: none;
	order: 1;
	flex: 0 0 42px;
	width: 42px;
	height: 24px;
	margin: 0;
	padding: 3px;
	border: 0;
	border-radius: 999px;
	background: ${cssVariables.colors.borders.default};
	cursor: pointer;
	transition: background 120ms ease;

	&::before {
		content: '';
		display: block;
		width: 18px;
		height: 18px;
		border-radius: 50%;
		background: ${cssVariables.colors.background.secondary};
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.16);
		transition: transform 120ms ease;
	}

	&:checked {
		background: ${cssVariables.colors.brand.secondary};
	}

	&:checked::before {
		transform: translateX(18px);
	}

	&:focus-visible {
		outline: 2px solid ${cssVariables.colors.brand.secondary};
		outline-offset: 3px;
	}

	@media (prefers-reduced-motion: reduce) {
		&,
		&::before {
			transition: none;
		}
	}
`

const MerchantList = styled.div`
	display: flex;
	flex-wrap: wrap;
	flex-direction: row;
	width: 100%;
	.Avatar + .Avatar {
		border-top: 1px solid ${cssVariables.colors.borders.divider};
	}
	.Avatar:last-child {
		margin-bottom: 40px;
	}
`

const PaddingAdder = styled.div`
	padding: ${({ compactTopSpacing }) => (compactTopSpacing ? '16px 66px 72px' : '42px 66px 72px')};
`

const WarningMessage = styled.p`
	text-align: center;
	color: ${cssVariables.colors.text.secondary};
	margin: ${cssVariables.spacings.xxl} 0 0 0;
`

const ErrorMessageStyled = styled(ErrorMessage)`
	margin-top: 20px;
`

const MerchantPicker = ({ myMerchants, onClose, selectedCountry, compactTopSpacing = false }) => {
	const { t: translate } = useTranslation()
	const [merchantName, setMerchantName] = useState('')
	const [searchByPid, setSearchByPid] = useState(false)
	const { currentMerchantID } = getCurrentMerchantID()
	const navigate = useNavigate()
	const location = useLocation()
	const { canSearchMerchants, canSearchMerchantsByPid, allowedMerchantCountries } = usePermissions()
	const pidMode = Boolean(canSearchMerchantsByPid && searchByPid)
	const { currentMerchantCountryCode } = useCurrentMerchantCountryCode()
	const merchantSearchCountry =
		selectedCountry ||
		getMerchantSearchCountry({
			allowedMerchantCountries,
			currentMerchantCountryCode,
			myMerchants,
		})

	const {
		merchantList: searchedMerchants,
		error,
		status,
	} = useMerchantSearch({
		value: merchantName,
		country: merchantSearchCountry,
		pidMode,
		enabled: canSearchMerchants,
	})

	const merchantList = canSearchMerchants ? searchedMerchants : myMerchants.map(merchant => ({ merchant }))

	const handleMerchantClick = useCallback(
		(id, country) => {
			setMerchantIdAndCountryCodeCookies(id, country)
			setTag('currentMerchantID', id)
			setTag('currentMerchantCountryCode', country)

			if (onClose) {
				onClose()
			}

			// If the user is currently creating or editing a campaign, redirect to campaigns list instead
			const { pathname } = location
			if (pathname.includes('campaigns/edit') || pathname.includes('campaigns/create')) {
				navigate('/campaigns')
			} else {
				window.location.reload()
			}
			onClose()
		},
		[location, navigate, onClose],
	)

	const onKeyPressed = useCallback(
		event => {
			// 13 ascii code for RETURN
			if (event.keyCode === 13) {
				const filteredMerchantList = !merchantList
					? []
					: merchantList.filter(merchant => merchant.target !== 'merchant:*')
				if (filteredMerchantList.length > 0) {
					const {
						merchant: { id, country },
					} = filteredMerchantList[0]
					handleMerchantClick(id, country)
				}
			}
			// 27 ascii code for ESC
			if (event.keyCode === 27) {
				if (onClose) {
					onClose()
				}
			}
		},
		[merchantList, handleMerchantClick, onClose],
	)

	useEffect(() => {
		document.addEventListener('keydown', onKeyPressed, false)
		return () => document.removeEventListener('keydown', onKeyPressed, false)
	}, [onKeyPressed])

	const handleInputOnChange = async ({ target: { value } }) => {
		setMerchantName(value)
	}
	if (myMerchants && myMerchants.length === 0 && !canSearchMerchants) {
		return null
	}

	let content
	let searchResult
	if (canSearchMerchants) {
		searchResult = merchantList ? merchantList.filter(permission => permission.merchant) : []
		content = (
			<MerchantInput
				aria-label={translate(pidMode ? 'merchantSelector.pidSearch.label' : 'merchantSelector.searchInput.label')}
				value={merchantName}
				name="merchantName"
				placeholder={translate(
					pidMode ? 'merchantSelector.pidSearch.placeholder' : 'merchantSelector.searchInput.placeholder',
				)}
				type="text"
				data-test-id="MerchantInput"
				onChange={handleInputOnChange}
				autoComplete="off"
				autoFocus
			/>
		)
	} else {
		searchResult = myMerchants.map(merchant => ({ merchant }))
		searchResult.sort((a, b) => {
			const merchantA = a.merchant.name.toUpperCase()
			const merchantB = b.merchant.name.toUpperCase()
			return merchantA < merchantB ? -1 : merchantA > merchantB ? 1 : 0 // eslint-disable-line no-nested-ternary
		})
		if (myMerchants && myMerchants.length > 5) {
			content = (
				<MerchantInput
					aria-label={translate(pidMode ? 'merchantSelector.pidSearch.label' : 'merchantSelector.searchInput.label')}
					value={merchantName}
					name="merchantName"
					placeholder={translate(
						pidMode ? 'merchantSelector.pidSearch.placeholder' : 'merchantSelector.searchInput.placeholder',
					)}
					type="text"
					data-test-id="MerchantInput"
					onChange={handleInputOnChange}
					autoComplete="off"
					autoFocus
				/>
			)
			if (merchantName && merchantName !== '') {
				const options = { keys: ['merchant.name'] }
				const fuse = new Fuse(searchResult, options)
				searchResult = fuse.search(merchantName)
			}
		}
	}
	if (document !== null && document.body !== null) {
		return (
			<PaddingAdder compactTopSpacing={compactTopSpacing}>
				<form onSubmit={event => event.preventDefault()}>
					{canSearchMerchantsByPid && (
						<SearchModeLabel>
							<SearchModeSwitch
								type="checkbox"
								role="switch"
								checked={pidMode}
								onChange={event => {
									setSearchByPid(event.target.checked)
									setMerchantName('')
								}}
							/>
							{translate(pidMode ? 'merchantSelector.pidSearch.toggle' : 'merchantSelector.searchInput.nameMode')}
						</SearchModeLabel>
					)}
					{content}
				</form>
				{merchantList && searchResult.length === 0 && (
					<WarningMessage>
						{translate(
							pidMode
								? status === 'FORBIDDEN'
									? 'merchantSelector.pidSearch.forbidden'
									: 'merchantSelector.pidSearch.notFound'
								: 'merchantSelector.searchInput.noResults',
						)}
					</WarningMessage>
				)}
				{error && <ErrorMessageStyled error={error} />}
				{!error && (
					<MerchantList>
						{searchResult.map(({ merchant: { name, logoUrl, id, country, classification } }, i) => {
							const isActive = id === currentMerchantID
							return (
								<AnimatedItem data-test-id="Avatar" className="Avatar" key={id} tabIndex={i}>
									<Avatar
										{...{
											logoUrl,
											isActive,
											name,
											country,
											classification,
										}}
										onClick={() => handleMerchantClick(id, country)}
									/>
								</AnimatedItem>
							)
						})}
					</MerchantList>
				)}
			</PaddingAdder>
		)
	}
	return null
}

MerchantPicker.propTypes = {
	permissions: PropTypes.array,
	onClose: PropTypes.func,
	selectedCountry: PropTypes.string,
	compactTopSpacing: PropTypes.bool,
}

export { MerchantPicker }
